import {
  KdsButton,
  KdsEmptyContent,
  KdsSearchbar,
} from "@kazaar-platform/design-system";
import { RiAddCircleLine } from "@remixicon/react";
import { useTranslate } from "@tolgee/react";
import { concat, isEmpty } from "lodash";
import { useEffect, useState } from "react";

import { CardAddress } from "@/components/CardAddress";
import { type AddressReadDto } from "@/config/apiClient";
import { useAddress, useUser } from "@/hooks";
import { type AddressListProps } from "./AddressList.types";

export const AddressList = ({
  selectedAddresses,
  onSelect,
  onPressEdit,
}: AddressListProps) => {
  const { t } = useTranslate();

  // Query Hooks
  const { useGetUserDefaultAddress, useSetDefaultAddress } = useUser();
  const { useGetAddress } = useAddress();
  const { data: userDefaultAddress, isPending: isFetchingDefaultAddress } =
    useGetUserDefaultAddress();
  const { mutateAsync: setUserDefaultAddress } = useSetDefaultAddress();
  const {
    data: organisationAddresses,
    isPending: isFetchingOrganisationAddresses,
  } = useGetAddress();

  // Redefine variables
  const addresses = organisationAddresses?.data;
  const defaultAddress = userDefaultAddress?.data;

  // States
  const [searchValue, setSearchValue] = useState<string>("");
  const [filteredAddresses, setFilteredAddresses] = useState<
    AddressReadDto[] | undefined
  >(undefined);

  // Set Filtered Addresses (Default address in first position)
  useEffect(() => {
    if (isEmpty(addresses) || !addresses) {
      return;
    }

    // Find Default Address in organisation address
    const userDefaultAddress = addresses.find(
      (address) => address.id === defaultAddress?.addressId
    );

    // Set Organisation Addresses if no default address found or Reorganise Addresses
    if (!userDefaultAddress) {
      setFilteredAddresses(addresses);
    } else {
      const addressesWithoutDefaultAddress = addresses.filter(
        (address) => address.id !== userDefaultAddress.id
      );
      setFilteredAddresses([
        userDefaultAddress,
        ...addressesWithoutDefaultAddress,
      ]);
    }
  }, [addresses, defaultAddress]);

  // Handle Search
  const handleSearch = (v: string) => {
    setSearchValue(v);

    if (v) {
      const addressesArray = addresses?.filter(
        (address) =>
          !!address.address.toLowerCase().includes(v.toLowerCase()) ||
          !!address.city.toLowerCase().includes(v.toLowerCase()) ||
          !!address.recipientName?.toLowerCase().includes(v.toLowerCase()) ||
          !!address.name.toLowerCase().includes(v.toLowerCase())
      );
      setFilteredAddresses(addressesArray);
    } else {
      setFilteredAddresses(addresses);
    }
  };

  // Handle Update User Default Address
  const handleUpdateUserDefaultAddress = async (addressId: string) => {
    await setUserDefaultAddress({
      newDefaultAddressId: addressId,
    });
  };

  // Handle Address Card Press
  const handleAddressCardPress = (address: AddressReadDto) => {
    if (
      selectedAddresses.some(
        (selectedAddress) =>
          selectedAddress.name === address.name &&
          selectedAddress.recipientName === address.recipientName &&
          selectedAddress.address === address.address
      )
    ) {
      const updatedArray = selectedAddresses.filter(
        (selectedAddress) =>
          selectedAddress.name !== address.name &&
          selectedAddress.recipientName !== address.recipientName &&
          selectedAddress.address !== address.address
      );
      onSelect(updatedArray);
    } else {
      onSelect(concat(selectedAddresses, [address]));
    }
  };

  // Return null when fetching
  if (isFetchingOrganisationAddresses || isFetchingDefaultAddress) {
    return null;
  }

  // Return Empty Block when no address.
  if (!addresses || isEmpty(addresses)) {
    return (
      <KdsEmptyContent
        title={t("addressDrawer.addressListEmpty.title")}
        description={t("addressDrawer.addressListEmpty.subtitle")}
        linkProps={{
          onPress: () => onPressEdit(undefined),
          children: t("buttons.createNewAddress"),
          href: "#",
        }}
      />
    );
  }

  return (
    <>
      <div className="sticky left-0 top-0 z-30 flex items-center justify-between gap-sp-xl bg-white pb-sp-md">
        <KdsSearchbar
          aria-label={t("inputs.searchAddress.label")}
          placeholder={t("inputs.searchAddress.label")}
          onChange={(e) => handleSearch(e.target.value)}
          value={searchValue}
          onClear={() => setSearchValue("")}
        />

        <KdsButton
          variant="outlined"
          iconLeft={RiAddCircleLine}
          onPress={() => onPressEdit(undefined)}
        >
          {t("buttons.createNewAddress")}
        </KdsButton>
      </div>

      <div className="flex flex-col gap-sp-xs py-sp-xs">
        {filteredAddresses?.map((address) => (
          <CardAddress
            key={address.id}
            addressDetails={address}
            isUsersDefaultAddress={address.id === defaultAddress?.addressId}
            isSelected={selectedAddresses.some(
              (selectedAddress) =>
                selectedAddress.address === address.address &&
                selectedAddress.recipientName === address.recipientName &&
                selectedAddress.postalCode === address.postalCode
            )}
            isPressable
            isWithCheckbox
            onCardPress={() => handleAddressCardPress(address)}
            onEdit={() => onPressEdit(address)}
            onChangeDefaultAddress={() =>
              void handleUpdateUserDefaultAddress(address.id)
            }
          />
        ))}
      </div>
    </>
  );
};
